import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useTRPC } from '@/lib/trpc'
import { useAutoGrowTextarea } from '@/lib/use-auto-grow-textarea'
import { cn } from '@/lib/utils'
import {
  MessageSendAttemptTracker,
  type OutboundSocialMessage,
} from '@/social/message-send-attempt'
import {
  createMessageTimeline,
  mergeLatestMessagePage,
  mergeOlderMessagePage,
  mergeSequencedMessages,
} from '@/social/message-timeline'
import { getPublicUserLabels } from '@/social/public-user-labels'
import { ReadReceiptTracker } from '@/social/read-receipt-tracker'
import { parseRoomInvite } from '@/social/room-invite'
import {
  useInvalidateSocial,
  useSocialWorkspace,
} from '@/social/social-provider'
import type { PublicSocialUser, SocialMessage } from '@/social/types'
import { useSocialText } from '@/social/use-social-text'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Gamepad2,
  Loader2,
  MessageCircle,
  Send,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import {
  ConversationNotificationMuteButton,
} from './conversation-notification-mute-button'
import { MessageRenderer } from './message-renderer'
import { SocialAvatar } from './social-avatar'

function newClientMessageId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    return crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function ConversationPanel({
  connectionId,
  friend,
  canMuteNotifications,
  notificationsMuted,
  currentUserId,
  onBack,
  onClose,
  className,
}: {
  connectionId: string
  friend: PublicSocialUser
  canMuteNotifications: boolean
  notificationsMuted: boolean
  currentUserId: string
  onBack?: () => void
  onClose?: () => void
  className?: string
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const text = useSocialText()
  const invalidateSocial = useInvalidateSocial()
  const { drafts, setDraft, clearDraftIfMatches, publishDataEvent }
    = useSocialWorkspace()
  const draft = drafts[connectionId] ?? ''
  const composerRef = useAutoGrowTextarea(draft)
  const friendLabels = getPublicUserLabels(friend)
  const messagesQuery = useQuery({
    ...trpc.socialRouter.getMessages.queryOptions({
      connectionId,
      limit: 50,
    }),
    refetchInterval: () =>
      typeof document !== 'undefined' && document.visibilityState === 'hidden'
        ? 30_000
        : 4_000,
    refetchIntervalInBackground: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  })
  const latestPage = messagesQuery.data as
    | {
      connectionId: string
      messages?: SocialMessage[]
      hasMore?: boolean
      peerReadThroughSeq?: number
    }
    | undefined
  const [messageTimeline, setMessageTimeline] = useState(() =>
    createMessageTimeline<SocialMessage>(connectionId),
  )
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [confirmedNotificationsMuted, setConfirmedNotificationsMuted]
    = useState(notificationsMuted)

  useEffect(() => {
    setMessageTimeline(current =>
      current.connectionId === connectionId
        ? current
        : createMessageTimeline<SocialMessage>(connectionId),
    )
    setLoadingOlder(false)
  }, [connectionId])

  useEffect(() => {
    setConfirmedNotificationsMuted(notificationsMuted)
  }, [connectionId, notificationsMuted])

  useEffect(() => {
    if (latestPage?.connectionId !== connectionId)
      return
    setMessageTimeline(current =>
      mergeLatestMessagePage(
        current,
        connectionId,
        latestPage.messages ?? [],
        latestPage.peerReadThroughSeq ?? 0,
      ),
    )
  }, [connectionId, latestPage?.connectionId, latestPage?.messages, latestPage?.peerReadThroughSeq])

  const messages = useMemo(() => {
    const accumulated = messageTimeline.connectionId === connectionId
      ? messageTimeline.messages
      : []
    const latest = latestPage?.connectionId === connectionId
      ? (latestPage.messages ?? [])
      : []
    return mergeSequencedMessages(accumulated, latest)
  }, [connectionId, latestPage, messageTimeline])
  const olderHasMore = messageTimeline.connectionId === connectionId
    ? messageTimeline.olderHasMore
    : null
  const peerReadThroughSeq = Math.max(
    messageTimeline.connectionId === connectionId
      ? messageTimeline.peerReadThroughSeq
      : 0,
    latestPage?.connectionId === connectionId
      ? latestPage.peerReadThroughSeq ?? 0
      : 0,
  )
  const latestHasMore = latestPage?.connectionId === connectionId
    ? (latestPage.hasMore ?? false)
    : false
  const hasMore = olderHasMore ?? latestHasMore

  const sendMessage = useMutation(
    trpc.socialRouter.sendMessage.mutationOptions(),
  )
  const markReadMutation = useMutation(
    trpc.socialRouter.markRead.mutationOptions(),
  )
  const setConversationMutedMutation = useMutation(
    trpc.socialRouter.setConversationMuted.mutationOptions(),
  )
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const endRef = useRef<HTMLDivElement | null>(null)
  const connectionIdRef = useRef(connectionId)
  const markReadAsyncRef = useRef(markReadMutation.mutateAsync)
  const readReceiptTrackerRef = useRef(new ReadReceiptTracker())
  const sendAttemptRef = useRef(new MessageSendAttemptTracker())
  const [atBottom, setAtBottom] = useState(true)
  const [readRetryEpoch, setReadRetryEpoch] = useState(0)
  connectionIdRef.current = connectionId
  markReadAsyncRef.current = markReadMutation.mutateAsync

  useEffect(() => {
    readReceiptTrackerRef.current.reset()
  }, [connectionId])

  useEffect(() => {
    const allowRetry = () => {
      if (
        document.visibilityState !== 'visible'
        || !document.hasFocus()
      ) {
        return
      }
      readReceiptTrackerRef.current.allowLifecycleRetry()
      setReadRetryEpoch(current => current + 1)
    }
    window.addEventListener('focus', allowRetry)
    document.addEventListener('visibilitychange', allowRetry)
    return () => {
      window.removeEventListener('focus', allowRetry)
      document.removeEventListener('visibilitychange', allowRetry)
    }
  }, [connectionId])

  useEffect(() => {
    const element = endRef.current
    if (!element || typeof IntersectionObserver === 'undefined')
      return
    const observer = new IntersectionObserver(
      entries => setAtBottom(entries.some(entry => entry.isIntersecting)),
      { root: scrollRef.current, threshold: 0.75 },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [connectionId])

  useEffect(() => {
    const container = scrollRef.current
    if (!container || (!atBottom && messages.length > 1))
      return
    const frame = requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight
    })
    return () => cancelAnimationFrame(frame)
  }, [atBottom, connectionId, messages.length])

  useEffect(() => {
    const latest = messages.at(-1)
    if (
      !latest
      || !atBottom
      || document.visibilityState !== 'visible'
      || !document.hasFocus()
    ) {
      return
    }
    const throughSeq = readReceiptTrackerRef.current.begin(latest.seq)
    if (throughSeq === null)
      return
    void markReadAsyncRef.current({
      connectionId,
      throughSeq,
    }).then(
      () => {
        if (connectionIdRef.current !== connectionId)
          return
        readReceiptTrackerRef.current.succeed(throughSeq)
        publishDataEvent('read.advanced', connectionId)
        void invalidateSocial().catch(() => null)
      },
      () => {
        if (connectionIdRef.current === connectionId)
          readReceiptTrackerRef.current.reject(throughSeq)
      },
    )
  }, [
    atBottom,
    connectionId,
    invalidateSocial,
    messages,
    publishDataEvent,
    readRetryEpoch,
  ])

  const parsedInvite = useMemo(
    () =>
      parseRoomInvite(
        draft,
        typeof window === 'undefined' ? undefined : window.location.origin,
      ),
    [draft],
  )
  const outboundPayload = useMemo<OutboundSocialMessage | null>(() => {
    const normalized = draft.trim()
    if (!normalized)
      return null
    return parsedInvite
      ? {
          type: 'room_invite',
          roomCode: parsedInvite.roomCode,
          caption: parsedInvite.caption,
        }
      : { type: 'text', text: normalized }
  }, [draft, parsedInvite])

  useEffect(() => {
    sendAttemptRef.current.invalidateIfPayloadChanged(
      connectionId,
      outboundPayload,
    )
  }, [connectionId, outboundPayload])

  const loadOlder = useCallback(async () => {
    const oldestSequence = messages.at(0)?.seq
    if (!oldestSequence || loadingOlder || !hasMore)
      return
    setLoadingOlder(true)
    const container = scrollRef.current
    const previousHeight = container?.scrollHeight ?? 0
    try {
      const page = (await queryClient.fetchQuery(
        trpc.socialRouter.getMessages.queryOptions({
          connectionId,
          beforeSeq: oldestSequence,
          limit: 50,
        }),
      )) as {
        connectionId: string
        messages?: SocialMessage[]
        hasMore?: boolean
        peerReadThroughSeq?: number
      }
      if (
        page.connectionId !== connectionId
        || connectionIdRef.current !== connectionId
      ) {
        return
      }
      setMessageTimeline(current =>
        mergeOlderMessagePage(
          current,
          connectionId,
          page.messages ?? [],
          page.hasMore ?? false,
          page.peerReadThroughSeq ?? 0,
        ),
      )
      requestAnimationFrame(() => {
        if (container && connectionIdRef.current === connectionId)
          container.scrollTop += container.scrollHeight - previousHeight
      })
    }
    catch {
      toast.error(text('social.error', 'Something went wrong.'))
    }
    finally {
      setLoadingOlder(false)
    }
  }, [
    connectionId,
    hasMore,
    loadingOlder,
    messages,
    queryClient,
    text,
    trpc.socialRouter.getMessages,
  ])

  const handleSend = useCallback(async () => {
    if (!outboundPayload || sendMessage.isPending)
      return

    const clientMessageId = sendAttemptRef.current.getOrCreate(
      connectionId,
      outboundPayload,
      newClientMessageId,
    )
    const sentDraftSnapshot = draft
    try {
      await (outboundPayload.type === 'room_invite'
        ? sendMessage.mutateAsync({
            connectionId,
            clientMessageId,
            type: 'room_invite',
            roomCode: outboundPayload.roomCode,
            caption: outboundPayload.caption,
          })
        : sendMessage.mutateAsync({
            connectionId,
            clientMessageId,
            type: 'text',
            text: outboundPayload.text,
          }))
    }
    catch {
      toast.error(
        text('social.toast.messageFailed', 'Could not send the message.'),
      )
      return
    }
    sendAttemptRef.current.markSucceeded(clientMessageId)
    clearDraftIfMatches(connectionId, sentDraftSnapshot)
    publishDataEvent('message.committed', connectionId)
    await invalidateSocial().catch(() => null)
  }, [
    clearDraftIfMatches,
    connectionId,
    draft,
    invalidateSocial,
    outboundPayload,
    publishDataEvent,
    sendMessage,
    text,
  ])

  const handleNotificationsMutedChange = useCallback(
    async (muted: boolean) => {
      if (
        !canMuteNotifications
        || friend.isAdministrator
        || setConversationMutedMutation.isPending
      ) {
        return
      }
      try {
        const result = await setConversationMutedMutation.mutateAsync({
          connectionId,
          muted,
        })
        setConfirmedNotificationsMuted(result.notificationsMuted)
        publishDataEvent('conversation.notifications.changed', connectionId)
        await invalidateSocial().catch(() => null)
        toast.success(
          result.notificationsMuted
            ? text(
                'social.toast.notificationsMuted',
                'Notifications muted for this conversation.',
              )
            : text(
                'social.toast.notificationsUnmuted',
                'Notifications enabled for this conversation.',
              ),
        )
      }
      catch {
        toast.error(
          text(
            'social.toast.notificationPreferenceFailed',
            'Could not update notification settings.',
          ),
        )
      }
    },
    [
      canMuteNotifications,
      connectionId,
      friend.isAdministrator,
      invalidateSocial,
      publishDataEvent,
      setConversationMutedMutation,
      text,
    ],
  )

  return (
    <section
      className={cn(
        'flex min-h-0 flex-1 flex-col overflow-hidden bg-background',
        className,
      )}
      aria-label={friendLabels.primary}
    >
      <header className="flex h-14 shrink-0 items-center gap-2 border-b px-3">
        {onBack && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={onBack}
            title={text('social.actions.back', 'Back')}
          >
            <ArrowLeft className="size-4" />
            <span className="sr-only">
              {text('social.actions.back', 'Back')}
            </span>
          </Button>
        )}
        <SocialAvatar name={friendLabels.avatar} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">
            {friendLabels.primary}
          </p>
          {friendLabels.secondary && (
            <p className="truncate text-xs text-muted-foreground">
              {friendLabels.secondary}
            </p>
          )}
        </div>
        <ConversationNotificationMuteButton
          canMuteNotifications={canMuteNotifications}
          isAdministrator={friend.isAdministrator}
          notificationsMuted={confirmedNotificationsMuted}
          pending={setConversationMutedMutation.isPending}
          onToggle={handleNotificationsMutedChange}
        />
        {onClose && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={onClose}
            title={text('social.actions.close', 'Close')}
            data-testid="social-conversation-close"
          >
            <X className="size-4" />
            <span className="sr-only">
              {text('social.actions.close', 'Close')}
            </span>
          </Button>
        )}
      </header>

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        {messagesQuery.isPending ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : messagesQuery.isError ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="text-sm text-destructive">
              {text('social.error', 'Something went wrong.')}
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => messagesQuery.refetch()}
            >
              {text('social.retry', 'Try again')}
            </Button>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-6 text-center">
            <span className="mb-3 flex size-12 items-center justify-center rounded-full bg-muted">
              <MessageCircle className="size-5 text-muted-foreground" />
            </span>
            <p className="text-sm font-medium">
              {text('social.conversation.noMessages', 'Start the conversation')}
            </p>
            <p className="mt-1 max-w-xs text-xs text-muted-foreground">
              {text(
                'social.conversation.noMessagesDescription',
                'Chat here, then share a multiplayer room when you are ready to play.',
              )}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {hasMore && (
              <div className="flex justify-center pb-1">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={loadingOlder}
                  onClick={loadOlder}
                >
                  {loadingOlder && (
                    <Loader2 className="size-3.5 animate-spin" />
                  )}
                  {text(
                    'social.conversation.loadOlder',
                    'Load earlier messages',
                  )}
                </Button>
              </div>
            )}
            {messages.map(message => (
              <MessageRenderer
                key={message.id}
                message={message}
                currentUserId={currentUserId}
                peerReadThroughSeq={peerReadThroughSeq}
              />
            ))}
          </div>
        )}
        <div ref={endRef} className="h-px" aria-hidden />
      </div>

      <footer className="shrink-0 border-t bg-background p-3">
        {parsedInvite && (
          <div className="mb-2 flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-2.5 py-2 text-xs text-primary">
            <Gamepad2 className="size-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate">
              {text('social.conversation.roomDetected', 'Room invite ready')}
              {' · '}
              {parsedInvite.roomCode}
            </span>
          </div>
        )}
        <div className="flex items-end gap-2">
          <Textarea
            ref={composerRef}
            value={draft}
            onChange={event => setDraft(connectionId, event.target.value)}
            onKeyDown={(event) => {
              if (event.nativeEvent.isComposing)
                return
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                handleSend()
              }
            }}
            placeholder={text(
              'social.conversation.messagePlaceholder',
              'Write a message or paste a room link…',
            )}
            maxLength={2_000}
            className="max-h-32 min-h-10 resize-none py-2"
            rows={1}
            disabled={sendMessage.isPending}
          />
          <Button
            type="button"
            size="icon"
            className="size-10"
            disabled={!draft.trim() || sendMessage.isPending}
            onClick={handleSend}
            title={text('social.conversation.send', 'Send')}
          >
            {sendMessage.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Send className="size-4" />
            )}
            <span className="sr-only">
              {text('social.conversation.send', 'Send')}
            </span>
          </Button>
        </div>
        <p className="mt-1.5 text-[10px] text-muted-foreground">
          {text(
            'social.conversation.sendHint',
            'Enter to send · Shift+Enter for a new line',
          )}
        </p>
      </footer>
    </section>
  )
}
